// Ride modal — one simple <dialog> for editing an existing ride and for adding a
// manual one (source `manual`). Delete is an inline-confirm step inside the
// footer rather than a row action, so there's nothing destructive to fat-finger
// while scrolling the list. Delete is hidden in new-entry mode.

import { putRide, deleteRide } from './db.js';
import { fmtDuration, parseDecimal, parseDuration, avgSpeedKmh } from './dataProcessor.js';

let dialog = null;
let els = null;
let current = null;   // the ride being edited; null in new-entry mode
let onChange = async () => {};

export function initRideModal({ onChange: cb } = {}) {
  dialog = document.getElementById('ride-dialog');
  if (!dialog) return;
  if (cb) onChange = cb;

  els = {
    title: document.getElementById('ride-dialog-title'),
    date: document.getElementById('rd-date'),
    rideTitle: document.getElementById('rd-title'),
    distance: document.getElementById('rd-distance'),
    duration: document.getElementById('rd-duration'),
    speed: document.getElementById('rd-speed'),
    deleteBtn: document.getElementById('rd-delete'),
    footMain: document.getElementById('rd-foot-main'),
    footConfirm: document.getElementById('rd-foot-confirm'),
  };

  // Clear a custom validity message as soon as the user edits the field.
  for (const id of ['rd-distance', 'rd-duration', 'rd-speed']) {
    document.getElementById(id).addEventListener('input', e => e.target.setCustomValidity(''));
  }

  document.getElementById('ride-form').addEventListener('submit', onSubmit);
  document.getElementById('ride-dialog-close').addEventListener('click', () => dialog.close());
  document.getElementById('rd-cancel').addEventListener('click', () => dialog.close());
  document.getElementById('rd-delete').addEventListener('click', armDelete);
  document.getElementById('rd-delete-cancel').addEventListener('click', resetConfirm);
  document.getElementById('rd-delete-confirm').addEventListener('click', onDelete);

  // Backdrop click (target is the <dialog> itself) and native Esc both close.
  dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
  dialog.addEventListener('close', resetConfirm);
}

export function openRideEditor(ride) {
  if (!dialog) return;
  current = ride;
  resetConfirm();
  els.title.textContent = 'Edit ride';
  els.deleteBtn.classList.remove('hidden');
  els.date.value = String(ride.date ?? '').slice(0, 10);
  els.rideTitle.value = ride.title ?? '';
  els.distance.value = toCommaField(ride.distance_km);
  els.duration.value = ride.duration_seconds > 0 ? fmtDuration(ride.duration_seconds) : '';
  els.speed.value = ride.speed_kmh > 0 ? toCommaField(ride.speed_kmh) : '';
  clearValidity();
  dialog.showModal();
}

export function openNewRide() {
  if (!dialog) return;
  current = null;
  resetConfirm();
  els.title.textContent = 'New entry';
  els.deleteBtn.classList.add('hidden'); // nothing to delete yet
  els.date.value = todayIso();
  els.rideTitle.value = '';
  els.distance.value = '';
  els.duration.value = '';
  els.speed.value = '';
  clearValidity();
  dialog.showModal();
}

async function onSubmit(e) {
  e.preventDefault(); // keep the dialog open; we persist then close ourselves

  const distanceKm = parseDecimal(els.distance.value);
  const durationSeconds = parseDuration(els.duration.value);
  const speedField = els.speed.value.trim();
  const speedKmh = speedField === '' ? null : parseDecimal(speedField);

  // Field-level validation (date required is native). Lenient parsers above
  // accept either decimal separator; surface anything that still won't parse.
  els.distance.setCustomValidity(distanceKm > 0 ? '' : 'Enter a distance in km, e.g. 42,1.');
  els.duration.setCustomValidity(durationSeconds === null ? 'Use h:mm:ss, e.g. 1:32:05.' : '');
  els.speed.setCustomValidity(speedField !== '' && speedKmh === null ? 'Enter a speed in km/h, or leave blank.' : '');
  if (!els.distance.reportValidity()) return;
  if (!document.getElementById('ride-form').reportValidity()) return;

  const base = current ? { ...current } : { id: newManualId(), source: 'manual' };
  const ride = {
    ...base,
    date: current ? resolveDate(current.date, els.date.value) : els.date.value,
    title: els.rideTitle.value.trim(),
    distance_km: distanceKm,
    duration_seconds: durationSeconds,
    speed_kmh: (speedKmh ?? avgSpeedKmh(distanceKm, durationSeconds)) ?? 0,
  };

  await putRide(ride);
  await onChange();
  dialog.close();
}

// Number → form string with an EU comma separator, matching the list display.
function toCommaField(n) {
  return n == null ? '' : String(n).replace('.', ',');
}

function clearValidity() {
  els.distance.setCustomValidity('');
  els.duration.setCustomValidity('');
  els.speed.setCustomValidity('');
}

function todayIso() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function newManualId() {
  const rnd = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `manual-${rnd}`;
}

async function onDelete() {
  if (!current) return;
  await deleteRide(current.id);
  await onChange();
  dialog.close();
}

// Keep the original timestamp when the calendar day is unchanged, so editing an
// unrelated field (e.g. the title) doesn't silently drop a Wahoo ride's time.
function resolveDate(originalDate, inputValue) {
  if (originalDate && String(originalDate).slice(0, 10) === inputValue) {
    return originalDate;
  }
  return inputValue;
}

function armDelete() {
  els.footMain.classList.add('hidden');
  els.footConfirm.classList.remove('hidden');
}

function resetConfirm() {
  els?.footConfirm.classList.add('hidden');
  els?.footMain.classList.remove('hidden');
}
